//ANCHOR - Imports
import { AfterViewInit, Component, OnChanges, OnInit, Signal, SimpleChanges, computed, inject, input, output } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Subject, map, of, switchMap, tap } from 'rxjs';

import { InformationPlanet } from '../../interfaces/information-planets';
import { InformationPopulation } from '../../interfaces/information-population';
import { InformationStarSystem } from '../../interfaces/information-star-system';
import { InformationWarpPoint } from '../../interfaces/information-warp-point';
import { Race } from '../../interfaces/race';
import { Star, StarSystem } from "../../interfaces/star-system";

import { InformationPlanetService } from '../../services/information-planet.service';
import { InformationWarpPointService } from '../../services/information-warp-point.service';
import { RaceService } from '../../services/race.service';
import { SessionService } from '../../services/session.service';
import { StarSystemService } from '../../services/star-system.service';
import { InformationPopulationService } from '../../services/information-population.service';

@Component({
  selector: 'star-system-display',
  templateUrl: './star-system-display.component.html',
  standalone: false
})
export class StarSystemDisplayComponent {
  informationPlanetsService = inject(InformationPlanetService);
  informationPopulationService = inject(InformationPopulationService);
  informationWarpPointService = inject(InformationWarpPointService);
  raceService = inject(RaceService);
  session = inject(SessionService);
  starSystemService = inject(StarSystemService);
  informationStarSystem = input.required<InformationStarSystem>();
  informationStarSystem$ = toObservable(this.informationStarSystem);
  readonly displayMoons = input<boolean>(false);
  readonly starSystemId = output<string>();

  race: Race;
  starSystem = toSignal<StarSystem>(this.informationStarSystem$.pipe(
    switchMap((iss) => {
      if (iss) {
        return this.starSystemService.getStarSystem(iss.starSystemId);
      }
      return of(undefined);
    }),
    tap((starSystem) => {
      if (starSystem) {
        let stars = starSystem.stars;
        if (stars.length > 0) {
          this.starTooltip(stars[0]);
          if (stars.length > 1) {
            this.starTooltip(stars[1]);
          }
        }
      }
    })
  ));

  displayControls = computed(() => {
    let workingStarSystem = this.starSystem();
    let hasStars = workingStarSystem ? (workingStarSystem?.stars.length > 0) : false;
    let primaryHasPlanets = false;
    let primarySurveyElements = Object.assign({}, this.baseSurveyElements());
    let secondaryHasPlanets = false;
    let secondarySurveyElements = Object.assign({}, this.baseSurveyElements());

    if (hasStars) {
      primaryHasPlanets = (workingStarSystem!.stars[0].planets.length > 0);
      if (primaryHasPlanets) {
        primarySurveyElements = this.identifyStarSurveyElements(workingStarSystem!.stars[0]);
      }

      secondaryHasPlanets = workingStarSystem!.stars.length === 2 &&
        workingStarSystem!.stars[1].planets.length > 0;
      if (secondaryHasPlanets) {
        secondarySurveyElements = this.identifyStarSurveyElements(workingStarSystem!.stars[1]);
      }
    }

    return {
      hasStars: hasStars,
      primaryHasPlanets: primaryHasPlanets,
      primarySurveyElements: primarySurveyElements,
      secondaryHasPlanets: secondaryHasPlanets,
      secondarySurveyElements: secondarySurveyElements
    };
  });


  informationPlanets: Signal<InformationPlanet[] | undefined>;
  informationPlanetHash: Signal<{ [key: string]: InformationPlanet; } | undefined>;
  // informationRacePopulations: Signal<InformationPopulation[] | undefined>;
  racePops: Signal<{ [key: string]: InformationPopulation; } | undefined>;
  // informationOtherPopulations: Signal<InformationPopulation[] | undefined>;
  otherPops: Signal<{ [key: string]: { [key: string]: InformationPopulation; }; } | undefined>;

  informationWarpPoints: Signal<InformationWarpPoint[] | undefined>;
  // TODO: [ISMNG17-1] fix this
  raceLookup = new Map();

  constructor () {
    this.race = this.session.getRace() as Race;

    this.informationPlanets = toSignal<InformationPlanet[]>(this.informationStarSystem$.pipe(
      switchMap((iss) => {
        if (iss) {
          return this.informationPlanetsService.getInformationPlanetsForRaceAndStarSystemIds(this.race._id, iss.starSystemId);
        }
        return of(undefined);
      }),
      takeUntilDestroyed(),
    ));

    this.informationPlanetHash = computed(() => {
      let initHash: { [key: string]: InformationPlanet; } = {};
      return this.informationPlanets()?.reduce((hash, ip) => {
        hash[ip.bodyId] = ip;
        return hash;
      }, initHash);
    });

    this.racePops = toSignal(
      this.informationPopulationService.getInformationPopulationForRaceId(this.race._id).pipe(
        map(ips => {
          let initHash: { [key: string]: InformationPopulation; } = {};
          return ips.reduce((hash, ip) => {
            hash[ip.planetId] = ip;
            return hash;
          }, initHash);
        }),
        takeUntilDestroyed(),
      )
    );

    this.otherPops = toSignal(
      this.informationPopulationService.getInformationPopulationForOtherRace(this.race._id).pipe(
        map(ips => {
          let initHash: { [key: string]: { [key: string]: InformationPopulation; }; } = {};
          let popHash = ips.reduce((hash, ip) => {
            let pops: { [key: string]: InformationPopulation; } = hash[ip.planetId] || {};
            pops[ip.populationForRaceId] = ip;
            hash[ip.planetId] = pops;
            let raceId = ip.populationForRaceId;
            if (!this.raceLookup.has(raceId)) {
              this.raceService.getRaceForId$(raceId).subscribe(
                race => this.raceLookup.set(raceId, race)
              );
            }
            return hash;
          }, initHash);
          return popHash;
        }),
        takeUntilDestroyed(),
      )
    );

    this.informationWarpPoints = toSignal<InformationWarpPoint[]>(this.informationStarSystem$.pipe(
      switchMap((iss) => {
        if (iss) {
          return this.informationWarpPointService.getInformationWarpPointsForRaceIdAndStarSystemId(this.race._id, iss.starSystemId);
        }
        return of(undefined);
      }),
      takeUntilDestroyed(),
    ));
  };

  starTooltip(star: Star) {
    let temp = '';
    if (star.distance) {
      temp = ', ' + star.distance + ' LM on ' + star.radian;
    }
    star.tooltip = star.type + temp + ' (' + star.strategicHex + ')';
  };

  baseSurveyElements() {
    return {
      h: false,   // has habitable planets to survey
      n: false,   // has non-habitable planets/moons to survey
      a: false    // has asteroids to survey
    };
  };

  changeStarSystemId(starSystemId: string | undefined) {
    if (starSystemId) {
      this.starSystemId.emit(starSystemId);
    }
  };

  identifyStarSurveyElements(star: Star) {
    let surveyElements = Object.assign({}, this.baseSurveyElements());
    if (star) {
      for (const planet of star.planets) {
        if (surveyElements.h && surveyElements.n && surveyElements.a) {
          break;
        }
        if (!surveyElements.a && planet.type === 'A') {
          surveyElements.a = true;
          continue;
        }
        if (!surveyElements.h && planet.hi > 0 && planet.hi <= 10) {
          surveyElements.h = true;
          continue;
        }
        if (!surveyElements.n && (planet.hi > 10 || (planet.moons && planet.moons.length > 0))) {
          surveyElements.n = true;
          continue;
        }
      }
    };
    return surveyElements;
  };
}
